import * as React from 'react';
import { Progress } from '@se-platform/ui/progress';
import { cn } from '@se-platform/ui/utils';

// The mark at the top of the margin: a two-digit step numeral, the same numeral
// at the smaller size the in-portal task screens use, or a short phrase set one
// word per line ("Who / you / are").
type RailMark =
  | { kind: 'numeral'; value: string; size?: 'default' | 'small' }
  | { kind: 'words'; lines: string[] };

interface MarginRailProps {
  mark: RailMark;
  // Small-caps label under the mark, e.g. "What you sell".
  label: string;
  // Fill of the progress track, 0–100.
  progress: number;
  // Where the reader is, in words: "Step 1 of 4", "Question 2 of 3", "First task, 1 of 2".
  position: string;
  // One orienting sentence in Deep Navy, directly under the position.
  note?: React.ReactNode;
  // A block separated by a hairline, for longer asides or the searched-on summary.
  aside?: React.ReactNode;
}

/**
 * The left margin of a "way in" screen. It carries the oversized mark, the step
 * label, the progress track and the orienting copy, so the content column can
 * carry nothing but the question. Nothing in here is interactive except a link
 * the caller places in `aside`.
 */
export function MarginRail({ mark, label, progress, position, note, aside }: MarginRailProps) {
  return (
    <div>
      <RailMarkBlock mark={mark} />
      <div className="mt-5 font-display text-brand-eyebrow uppercase text-brand-muted">{label}</div>
      <Progress
        aria-label={position}
        value={progress}
        className="mt-[22px] h-[5px] w-[140px] bg-brand-line [&>div]:rounded-full [&>div]:bg-gradient-brand"
      />
      <div className="mt-2.5 text-brand-meta text-brand-muted">{position}</div>
      {note ? <div className="mt-[26px] text-brand-fine text-brand-navy">{note}</div> : null}
      {aside ? (
        <div className="mt-[30px] border-t border-brand-line pt-5 text-brand-note text-brand-muted">
          {aside}
        </div>
      ) : null}
    </div>
  );
}

function RailMarkBlock({ mark }: { mark: RailMark }) {
  if (mark.kind === 'words') {
    return (
      <div className="font-display text-brand-headline text-brand-black lg:text-brand-word">
        {mark.lines.map((currentLine) => (
          <React.Fragment key={currentLine}>
            {currentLine}
            <br />
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        // Stacked above the column below the frame width, the mark steps down a size
        // so the question still starts on a phone's first screen.
        'font-display text-brand-numeral-sm text-brand-black',
        mark.size !== 'small' && 'lg:text-brand-numeral',
      )}
    >
      {mark.value}
    </div>
  );
}

/**
 * The "Searched on" readout the match screen places in the margin: a heading row
 * with a Change link, then hairline-divided key/value pairs.
 */
export function RailSummary({
  heading,
  action,
  rows,
}: {
  heading: string;
  action?: React.ReactNode;
  rows: Array<{ key: string; value: string }>;
}) {
  return (
    <div>
      <div className="mb-3 flex items-baseline gap-2.5">
        <span className="flex-grow font-display text-brand-fact-label uppercase text-brand-muted">
          {heading}
        </span>
        {action}
      </div>
      {rows.map((currentRow) => (
        <div key={currentRow.key} className="border-t border-brand-line py-2">
          <div className="text-caption text-brand-muted">{currentRow.key}</div>
          <div className="mt-0.5 font-display text-brand-meta-strong text-brand-black">
            {currentRow.value}
          </div>
        </div>
      ))}
    </div>
  );
}
